import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { Link } from "react-router";
import { brandColors as colors, usePalette } from "@/lib/palette";
import { fonts } from "@/lib/fonts";
import { fadeUp, stagger } from "@/lib/animations";
import Blob from "@/components/ui/Blob";
import StitchDots from "@/components/ui/StitchDots";

export default function HeroVariantB() {
  const theme = usePalette();
  return (
    <section
      className="relative min-h-dvh flex flex-col items-center justify-center overflow-hidden pt-20 pb-16"
      style={{ backgroundColor: colors.cream }}
    >
      <Blob className="w-96 h-96 top-10 -right-20" color={theme.blob1} />
      <Blob className="w-80 h-80 bottom-20 -left-10" color={theme.blob2} />
      <Blob className="w-64 h-64 top-1/2 right-1/3" color={theme.blob3} />
      <StitchDots />

      <div className="relative z-10 max-w-4xl mx-auto px-6 w-full text-center flex-1 flex flex-col justify-center">
        <motion.div initial="hidden" animate="visible" variants={stagger}>
          <motion.p
            variants={fadeUp}
            custom={0}
            className="text-xs uppercase mb-4"
            style={{
              fontFamily: fonts.body,
              fontWeight: 700,
              letterSpacing: "0.25em",
              color: theme.accent,
            }}
          >
            Welcome, friend
          </motion.p>

          <motion.h1
            variants={fadeUp}
            custom={1}
            className="text-5xl sm:text-7xl lg:text-8xl leading-[1.05] mb-6"
            style={{
              fontFamily: fonts.logo,
              fontWeight: 400,
              color: colors.navy,
            }}
          >
            Loose threads,{" "}
            <span
              className="relative inline-block"
              style={{ color: theme.accent }}
            >
              big ideas
              <motion.svg
                className="absolute left-0 w-full"
                style={{ bottom: "-2rem", height: "5rem" }}
                viewBox="0 0 200 80"
                fill="none"
                preserveAspectRatio="none"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1.6, delay: 0.8 }}
              >
                <motion.path
                  d="M2 8 Q50 2, 100 7 T198 5 Q150 30, 100 50 T100 75"
                  stroke={theme.warm}
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeDasharray="6 4"
                  fill="none"
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.6, delay: 0.8 }}
                />
              </motion.svg>
            </span>
            <span style={{ color: theme.warm }}>!</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={2}
            className="text-base sm:text-lg leading-relaxed mb-10 max-w-xl mx-auto"
            style={{
              fontFamily: fonts.body,
              fontWeight: 500,
              color: colors.navy,
              opacity: 0.75,
              letterSpacing: "0.03em",
            }}
          >
            Step away from the noise and into a weekend of sewing and
            connection.
          </motion.p>

          <motion.div variants={fadeUp} custom={3} className="mb-6">
            <Link
              to="/retreats"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-white cursor-pointer transition-all duration-250 hover:shadow-xl hover:scale-105 active:scale-[0.98]"
              style={{
                fontFamily: fonts.button,
                fontWeight: 700,
                letterSpacing: "0.06em",
                textTransform: "uppercase" as const,
                fontSize: "0.75rem",
                backgroundColor: theme.accent,
              }}
            >
              Explore Retreats
              <ArrowRight size={16} />
            </Link>
          </motion.div>

          <motion.div
            variants={fadeUp}
            custom={4}
            className="inline-flex items-center gap-3 px-5 py-2.5 rounded-full"
            style={{
              backgroundColor: colors.cream,
              border: `1px solid ${colors.tan}55`,
            }}
          >
            <motion.span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: theme.accent }}
              animate={{ opacity: [1, 0.3, 1], scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.8 }}
            />
            <Calendar size={14} style={{ color: colors.navy, opacity: 0.6 }} />
            <span
              className="text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 600,
                color: colors.navy,
                opacity: 0.7,
              }}
            >
              Next retreat:
            </span>
            <span
              className="text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: colors.navy,
              }}
            >
              Oct 12–14
            </span>
            <span
              className="w-1 h-1 rounded-full"
              style={{ backgroundColor: theme.warm }}
            />
            <span
              className="text-sm"
              style={{
                fontFamily: fonts.body,
                fontWeight: 700,
                color: theme.accent,
              }}
            >
              3 seats left
            </span>
          </motion.div>
        </motion.div>
      </div>

      <motion.div
        className="relative z-10 max-w-5xl mx-auto px-6 w-full flex items-end justify-center gap-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 1.4 }}
      >
        {[
          { rot: -6, label: "Stitch & Sip", color: theme.blob1 },
          { rot: 3, label: "Pattern Lab", color: theme.blob2 },
          { rot: -2, label: "Quilt Camp", color: theme.blob3 },
        ].map((card, i) => (
          <div
            key={i}
            className="rounded-sm shadow-lg p-2 pb-6 relative hidden sm:block"
            style={{
              backgroundColor: colors.cream,
              transform: `rotate(${card.rot}deg)`,
              border: `1px solid ${colors.tan}25`,
            }}
          >
            <div
              className="w-32 h-24 rounded-sm"
              style={{ backgroundColor: card.color, opacity: 0.4 }}
            />
            <p
              className="absolute bottom-1 left-0 right-0 text-center text-xs"
              style={{
                fontFamily: fonts.heading,
                color: colors.navy,
                opacity: 0.7,
              }}
            >
              {card.label}
            </p>
          </div>
        ))}
        <p
          className="hidden sm:block ml-2 text-2xl rotate-[-4deg] self-center"
          style={{
            fontFamily: fonts.heading,
            color: theme.warm,
          }}
        >
          the retreats <br /> are magic ✨
        </p>
      </motion.div>
    </section>
  );
}
